import {Table} from 'notebook/tables/TableConstants';
import {AnyStructuredTable, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {GREEN, ORANGE, RED} from 'ui-library/Colors';

export interface TablePerformanceSummary {
  name: string;
  numCells: number;
  numRows: number;
  numColumns: number;
  colorHex: string;
  percentage: number;
}

export interface NotePerformanceSummary {
  numTables: number;
  numCells: number;
  tableSummaries: Array<TablePerformanceSummary>;
}

export const ENTRY_TABLE_CELL_COUNT_LIMIT = 35_000;
const ENTRY_TABLE_CELL_COUNT_LIMIT_WARNING = ENTRY_TABLE_CELL_COUNT_LIMIT * 0.5;

export const _forTesting = {
  getPlainTablePerformanceSummary,
  getStructuredTablePerformanceSummary,
};

function getColorHex(percentage: number): string {
  if (percentage < 0.25) {
    return GREEN;
  } else if (percentage < 0.5) {
    return ORANGE;
  }
  return RED;
}

function getPlainTablePerformanceSummary(table: Table): TablePerformanceSummary {
  const numRows = table.table.length;
  const numColumns = table.table[0].length;
  const numCells = numRows * numColumns;
  const percentage = numCells / ENTRY_TABLE_CELL_COUNT_LIMIT;
  const colorHex = getColorHex(percentage);

  return {
    name: table.name,
    numCells,
    numRows,
    numColumns,
    colorHex,
    percentage,
  };
}

function getStructuredTablePerformanceSummary(table: AnyStructuredTable): TablePerformanceSummary {
  const numRows = table.rows.length;
  const numColumns = table.columns.length;
  const numCells = numRows * numColumns;
  const percentage = numCells / ENTRY_TABLE_CELL_COUNT_LIMIT;
  const colorHex = getColorHex(percentage);

  return {
    name: table.name,
    numCells,
    numRows,
    numColumns,
    colorHex,
    percentage,
  };
}

export const TableLimitUtils = {
  sizeIsInWarningRange(numCells: number): boolean {
    return numCells > ENTRY_TABLE_CELL_COUNT_LIMIT_WARNING && numCells < ENTRY_TABLE_CELL_COUNT_LIMIT;
  },
  sizeIsOverMaximum(numCells: number): boolean {
    return numCells >= ENTRY_TABLE_CELL_COUNT_LIMIT;
  },

  analyzeNotePerformance(tables: Array<Table | AnyStructuredTable>): NotePerformanceSummary {
    let numCells = 0;
    let numTables = 0;
    const tableSummaries =
      tables?.map((table) =>
        // eslint-disable-next-line benchlint/no-in-operator
        'type' in table && table.type === NoteLinkedObjectType.STRUCTURED_TABLE
          ? getStructuredTablePerformanceSummary(table)
          : getPlainTablePerformanceSummary(table as Table)
      ) ?? [];

    for (const summary of tableSummaries) {
      numTables++;
      numCells += summary.numCells;
    }

    return {
      numTables,
      numCells,
      tableSummaries,
    };
  },
};
